import type { Module } from '@/types'

export const CHALLENGES = [
  'Subscription wastage — disabled users with licenses, inactive accounts, duplicate assignments',
  'Cost visibility — IT managers need to demonstrate cost reduction to the business',
  'Licensing complexity — customers struggle with what\'s included in E3 vs E1 vs Business Premium vs F-series',
  'Security posture gaps — IT managers and MSPs worried about whether every box is ticked',
  'Compliance configuration — CIS benchmarks, conditional access, MFA coverage',
  'Migration from non-Microsoft tools — moving antivirus, mail security, device management to M365 native',
]

export const COMMERCIAL_MODEL = {
  region: 'APAC',
  model: 'Fixed-fee engagement (preferred over monthly SaaS subscriptions)',
  suggested: '$500/month for 3–6 months to remediate 10 agreed items',
  incentive: 'Waive first 3 months — partner earns on remaining months',
  closeCondition: 'Sign-off when agreed items are complete',
  positioning: [
    'Value-add for customer retention',
    'Managed service inclusion',
    'Standalone professional services revenue',
  ],
}

export const MODULES: Module[] = [
  {
    id: 'subscription-optimisation',
    number: 1,
    title: 'Subscription Optimisation',
    tagline: 'The easiest win — immediate, tangible savings for any customer',
    icon: 'TrendingDown',
    estimatedMinutes: 12,
    overview:
      'The first thing to run on any customer. 365 Hound scans the M365 tenant via Graph API and surfaces wasted licensing spend — disabled users still holding seats, duplicate assignments, inactive accounts, and add-ons nobody is using. At renewal time, the customer reduces their licence count and pockets the savings.',
    customerChallenges: [0, 1, 2],
    steps: [
      {
        number: 1,
        title: 'Onboard the customer into 365 Hound',
        description:
          'Complete the enterprise app approval flow so 365 Hound can read the tenant via Microsoft Graph API. This grants read-only access to user accounts, licence assignments, and sign-in activity.',
        tips: ['This typically takes 5–10 minutes with a Global Admin present'],
      },
      {
        number: 2,
        title: 'Show the wastage on the Licensing screen',
        description:
          'Navigate to the Licensing screen and walk the customer through the wastage report. Look for disabled users with active licences, duplicate assignments (e.g. Business Premium + E3), inactive accounts (no sign-in for 30+ days), users not using desktop apps (F-series downgrade candidates), and unused add-ons like Power BI.',
        substeps: [
          'Disabled users still holding licences',
          'Duplicate licence assignments (e.g. Business Premium + Entra ID Plan 1)',
          'Inactive accounts — no sign-in for 30+ days',
          'Users not using desktop apps → F-series downgrade candidates',
          'Power BI or add-on licences assigned but unused for months',
          'Unlicensed users with OneDrive data (Microsoft started billing for this in 2025)',
        ],
        tips: [
          'Wastage becomes commercially meaningful at ~200–300 users',
          'Even below that threshold, the conversation is valuable for building trust',
        ],
      },
      {
        number: 3,
        title: 'Customer remediates — deallocate and archive',
        description:
          'The customer (or the partner under an MSP agreement) works through the wastage list: deallocating licences from disabled users, checking whether inactive accounts belong to people on leave, and archiving data for departed employees.',
        tips: ['Partners can offer this as a managed service deliverable'],
      },
      {
        number: 4,
        title: 'Confirm the reduction with a follow-up scan',
        description:
          'After remediation, run a fresh scan to confirm that wastage has been reduced. The before/after comparison is a powerful deliverable for the IT manager to show their leadership.',
      },
      {
        number: 5,
        title: 'At renewal: reduce the licence count',
        description:
          'When the renewal date approaches, reference the optimisation work and reduce the licence count accordingly. The customer realises direct cost savings and can reinvest them into new services or simply reduce spend.',
        tips: [
          'Use the Insights screen to track renewal dates and engage 2 months early',
          'Frame the savings as a business outcome, not a technical activity',
        ],
      },
    ],
    mermaidDiagram: `flowchart LR
    subgraph Onboard
        A[Connect 365 Hound\\nto tenant]
    end
    subgraph Discover
        B[Scan licences\\nvia Graph API] --> C[Review wastage\\nreport]
    end
    subgraph Remediate
        D[Present findings\\nto customer] --> E[Deallocate &\\narchive licences]
    end
    subgraph Renew
        F[Confirm savings\\nwith rescan] --> G[Reduce licence\\ncount at renewal]
    end
    Onboard --> Discover --> Remediate --> Renew`,
    realWorldExample: {
      scenario: 'A current customer with approximately 3,000 seats across the tenant',
      outcome: 'Saved 60,000 USD annually over 3 weeks of optimisation work',
    },
    keyTalkingPoints: [
      '"Whatever licences you\'re not using today are money you\'re already losing."',
      '"This takes one scan and one conversation — we can show you the numbers right now."',
      '"At renewal you\'ll be able to reduce your seat count with evidence to back it up."',
      '"Every IT manager has a boss. Being able to say \'I reduced our M365 costs by $X this year\' is powerful."',
    ],
    cadence: 'Enterprise customers: monthly review. Smaller customers: quarterly.',
    heroAngle:
      'Every IT manager has a boss. Being able to say "I reduced our M365 costs by $X this year" is powerful. The partner who enables that becomes indispensable.',
  },

  {
    id: 'cloud-security-uplift',
    number: 2,
    title: 'Cloud Security Uplift',
    tagline: 'Professional services packages built around CIS benchmark compliance and remediation',
    icon: 'ShieldCheck',
    estimatedMinutes: 15,
    overview:
      'Package a professional services engagement around bringing a customer\'s M365 and Intune environment into CIS benchmark compliance. 365 Hound does the scanning — you deliver the remediation. The tool gives you pass/fail/partial results per control, vendor references, and step-by-step audit instructions so you can confidently scope and price the work.',
    customerChallenges: [3, 4],
    steps: [
      {
        number: 1,
        title: 'Discovery — run the CIS benchmark scan',
        description:
          'Run 365 Hound\'s CIS benchmark scan against the customer\'s tenant. The tool queries M365 security settings, Intune configurations, and conditional access policies and returns a pass/fail/partial result for each control across Level 1 and Level 2.',
        tips: [
          'iOS and Windows 11 Intune configuration scanning is included',
          'Use the vendor references in the tool to back up your findings',
        ],
      },
      {
        number: 2,
        title: 'Present the discovery report',
        description:
          'Walk the customer through the failed and partial controls. Group them by risk area (identity, device, data) to make the remediation scope clear. This is the deliverable that justifies the engagement.',
        substeps: [
          'Failed CIS controls — highest priority',
          'Partial controls — quick wins',
          'Conditional access policy gaps',
          'MFA coverage gaps (especially admins and guests)',
          'PIM / permanently assigned privileged roles',
        ],
      },
      {
        number: 3,
        title: 'Remediate controls — professional services delivery',
        description:
          'Work through the failed controls with the customer. 365 Hound provides remediation steps for each control so you\'re not writing runbooks from scratch. Apply the changes to M365 settings, Intune policies, and conditional access.',
        tips: [
          'Create packages based on the number of failed controls and their complexity — smaller packages for targeted gaps, larger engagements for full remediation',
        ],
      },
      {
        number: 4,
        title: 'Reassess and hand over',
        description:
          'Trigger a reassessment scan to confirm that controls are now passing. Produce a before/after compliance report as the handover document. The customer has evidence of their security posture for audits, insurance, or internal governance.',
        tips: [
          'The delta report (pass/fail before vs after) is a powerful summary for leadership',
          'Schedule a follow-up scan cadence as part of the ongoing engagement',
        ],
      },
    ],
    mermaidDiagram: `flowchart LR
    subgraph Discover
        A[Run CIS\\nbenchmark scan] --> B[Review pass/fail\\ncontrols]
    end
    subgraph Present
        C[Walk customer through\\ndiscovery report] --> D[Agree scope\\nand price]
    end
    subgraph Remediate
        E[Fix failed controls\\nidentity · device · data]
    end
    subgraph Handover
        F[Reassess &\\nconfirm improvements] --> G[Deliver before/after\\ncompliance report]
    end
    Discover --> Present --> Remediate --> Handover`,
    keyTalkingPoints: [
      '"We run the scan, you get a pass/fail list with fix instructions. No guesswork."',
      '"The before/after compliance report is something the IT manager can show the board or their cyber insurance provider."',
      '"CIS Level 1 is the baseline most insurers and enterprise customers expect."',
      '"Package the engagement around the number of failed controls — customers can see exactly what they\'re paying to fix."',
    ],
    pricingNote: 'Scope packages based on control count and complexity. The before/after compliance report is a tangible deliverable for board-level or insurance reporting.',
    additionalServices: [
      'Conditional access policy review — dead policies (not evaluated in 90+ days), single overarching policies, new apps missing from block lists',
      'MFA coverage assessment — gaps in user/guest MFA, weak methods (phone/SMS), admins without MFA',
      'PIM review — permanently assigned privileged roles, global admins not under PIM control',
      'RBAC review — who has what access, service principals with excessive permissions',
      'Certificate and secret expiry — proactive alerts before enterprise app credentials expire',
    ],
  },

  {
    id: 'renewal-optimisation',
    number: 3,
    title: 'Renewal Optimisation',
    tagline: 'Turn a billing event into a strategic conversation — every year',
    icon: 'CalendarClock',
    estimatedMinutes: 10,
    overview:
      'Use 365 Hound\'s Insights screen to track renewal dates and indicative annual costs. Engage customers 2 months before renewal, run optimisation to reduce waste, and build a 12-month roadmap so the renewal becomes a strategic conversation rather than just an invoice.',
    customerChallenges: [0, 1, 2],
    steps: [
      {
        number: 1,
        title: 'Monitor renewal dates in the Insights screen',
        description:
          'The Insights screen shows upcoming licence renewal dates alongside indicative annual cost. Use this to build a 2-month forward-looking engagement calendar for your customer base. Cross-reference with your distributor or billing platform for a complete picture.',
        tips: ['Cross-reference with your distributor\'s renewal data to align billing and usage views'],
      },
      {
        number: 2,
        title: 'Engage the customer 2 months before renewal',
        description:
          'Reach out to the customer with a clear agenda: review what they\'re using, what they\'re not, and what\'s changed in their business. This gives enough time to remediate waste before the renewal locks in.',
        tips: [
          '2 months is the sweet spot — enough time to action changes, close enough to feel urgent',
        ],
      },
      {
        number: 3,
        title: 'Run optimisation to identify recoverable licences',
        description:
          'Run a subscription optimisation scan (Method 1) and present the wastage report. Quantify the saving the customer can achieve by reducing licence count at renewal.',
      },
      {
        number: 4,
        title: 'Build a 12-month roadmap',
        description:
          'Go beyond the immediate renewal. Map out what changes in the next 12 months: licences to add, services to migrate, security improvements to phase in. This positions the partner as a strategic advisor, not a reseller.',
        substeps: [
          'Licences to reduce or downgrade',
          'New licences or features to activate (e.g. Copilot pilot)',
          'Security or compliance work to phase in',
          'Migrations from third-party tools to M365 native',
        ],
      },
      {
        number: 5,
        title: 'Close the renewal as a strategic event',
        description:
          'The customer renews at the reduced licence count, with a clear plan for the year ahead. The partner is now embedded in the annual business cycle rather than just processing renewals.',
        tips: [
          '"We saved you $X last year. Here\'s what we\'re planning for next year." — this is the conversation that builds retention.',
        ],
      },
    ],
    mermaidDiagram: `flowchart LR
    subgraph Monitor
        A[Track renewal dates\\nin Insights screen]
    end
    subgraph Engage
        B[Contact customer\\n2 months before renewal] --> C[Run optimisation\\nscan]
    end
    subgraph Plan
        D[Present savings\\nopportunity] --> E[Build 12-month\\nroadmap]
    end
    subgraph Close
        F[Renew at reduced\\nlicence count] --> G[Deepen strategic\\npartnership]
    end
    Monitor --> Engage --> Plan --> Close`,
    keyTalkingPoints: [
      '"We can see your renewal date and estimated cost right now — let\'s plan ahead."',
      '"If we run the optimisation before renewal, you can reduce your seat count and save money."',
      '"The renewal is just one part of the conversation — let\'s map out what you need for the whole year."',
      '"Your distributor tracks your renewal date. We add the usage data layer on top so you go in informed."',
    ],
    cadence: 'Engage 2 months before each annual renewal. Monthly health checks keep the data fresh.',
  },

  {
    id: 'migration-roadmap',
    number: 4,
    title: 'Pre-Renewal Migration Roadmap',
    tagline: 'Find the third-party tools customers are paying for twice',
    icon: 'ArrowRightLeft',
    estimatedMinutes: 12,
    overview:
      'Use the Entra Apps screen in 365 Hound to surface all enterprise apps and their permissions in the customer\'s tenant. Identify where customers use third-party tools — antivirus, MDM, mail security — that have Microsoft equivalents already included in their licences. Build a migration roadmap that generates services revenue and positions the partner for Microsoft funding and co-sell.',
    customerChallenges: [5, 2],
    steps: [
      {
        number: 1,
        title: 'Review the Entra Apps screen',
        description:
          'The Entra Apps screen lists all enterprise applications in the customer tenant along with their API permissions. This makes it easy to spot competitive products — ManageEngine, third-party AV, mail security gateways — that could be replaced with M365 native capabilities.',
        tips: [
          'Look for apps with device management, email scanning, or identity permissions — these are migration candidates',
          'A future AI feature is planned to recommend M365 alternatives based on partner preferences',
        ],
      },
      {
        number: 2,
        title: 'Identify migration candidates',
        description:
          'Map the third-party tools found to their Microsoft equivalents. Common patterns include antivirus → Microsoft Defender, ManageEngine/other MDM → Microsoft Intune, third-party mail security → Exchange Online Protection / Defender for Office 365, and other device management → Intune.',
        substeps: [
          'Third-party antivirus → Microsoft Defender',
          'ManageEngine / other MDM → Microsoft Intune',
          'Third-party mail security gateway → Exchange Online Protection / Defender for Office 365',
          'Other device management tools → Intune',
          'Third-party identity / SSO tools → Microsoft Entra ID',
        ],
      },
      {
        number: 3,
        title: 'Propose the migration roadmap',
        description:
          'Present the migration opportunity to the customer. Frame it as: they\'re already paying for the Microsoft capabilities in their licences — the migration realises that investment and eliminates duplicate vendor spend.',
        tips: [
          '"You\'re paying for Defender in your E3. You\'re also paying for a third-party AV. We can consolidate that."',
        ],
      },
      {
        number: 4,
        title: 'Execute the migration — professional services',
        description:
          'Scope and deliver the migration as a professional services engagement. Defender, Intune, and EOP migrations are well-defined workloads with clear deliverables. This is where the partner earns the services revenue.',
      },
      {
        number: 5,
        title: 'Report the migration story to Microsoft',
        description:
          'Microsoft loves migration stories from the third-party ecosystem into M365 native tools. Report the win through the partner portal for co-sell recognition, funding opportunities, and cross-grade support.',
        tips: [
          'This positions the partner for Microsoft incentive funding on top of the services revenue',
          'Entra Apps migrations (especially to Entra ID) are particularly valued',
        ],
      },
    ],
    mermaidDiagram: `flowchart LR
    subgraph Discover
        A[Review Entra\\nApps screen] --> B[Identify non-Microsoft\\ntools in tenant]
    end
    subgraph Propose
        C[Map tools to\\nM365 equivalents] --> D[Present migration\\nroadmap]
    end
    subgraph Execute
        E[Agree scope\\nand budget] --> F[Deliver migration\\nDefender · Intune · EOP]
    end
    subgraph Reward
        G[Report to\\nMicrosoft] --> H[Earn partner\\nfunding]
    end
    Discover --> Propose --> Execute --> Reward`,
    keyTalkingPoints: [
      '"You\'re paying for Defender in your E3 licence. You\'re also paying for a separate AV. We can consolidate that."',
      '"The Entra Apps screen shows us every third-party tool in your tenant right now."',
      '"Microsoft funds partners who migrate customers off competitive tools. This isn\'t just a services win — it\'s a funding opportunity."',
      '"Every migration is a story Microsoft wants to hear. We report it together."',
    ],
  },

  {
    id: 'copilot-readiness',
    number: 5,
    title: 'Copilot Readiness Assessment',
    tagline: 'Identify the right pilot users before the customer guesses',
    icon: 'Sparkles',
    estimatedMinutes: 10,
    overview:
      'Use 365 Hound\'s Copilot screen to analyse user activity across Teams, Office, SharePoint, and email. The tool groups users by inferred job function, scores them by how actively they engage with the Microsoft ecosystem, and recommends the ideal pilot group — with estimated licensing costs. This turns a "who should get Copilot?" conversation into a data-driven recommendation.',
    customerChallenges: [1, 2],
    steps: [
      {
        number: 1,
        title: 'Run the Copilot readiness scan',
        description:
          'The Copilot screen analyses user activity across the M365 suite: Teams usage, Office document creation, SharePoint activity, and email volume. It groups users by inferred job function and scores them by how actively they contribute to and engage with the Microsoft ecosystem.',
        tips: [
          'If usernames appear as GUIDs instead of display names, the reporting API is anonymised — this is a quick diagnostic to flag and resolve with the customer',
        ],
      },
      {
        number: 2,
        title: 'Identify the recommended pilot group',
        description:
          'Review the recommended users — those who are actively contributing through Office documents, Teams meetings, and emails. These are the users who will get the most value from Copilot and are most likely to become internal champions.',
        tips: [
          '"These are your internal champions — they\'ll miss it if you take it away."',
        ],
      },
      {
        number: 3,
        title: 'Present the pilot proposal with cost estimate',
        description:
          'The tool estimates the licensing cost for the recommended group. Present a pilot proposal: deploy Copilot to the recommended users for 30–90 days, measure adoption and outcomes, then decide on broader rollout.',
        substeps: [
          'Recommended users and their job function grouping',
          'Estimated monthly and annual licence cost for the pilot group',
          'Success metrics to track during the pilot (prompts used, documents generated, meeting summaries)',
        ],
      },
      {
        number: 4,
        title: 'Review adoption and expand',
        description:
          'After the pilot period, review adoption data and user feedback. Use the outcomes to build the business case for broader rollout. The initial champions are your best advocates for the next wave.',
        tips: [
          'Partners can offer Copilot adoption coaching as a professional service alongside the licence sale',
          'Microsoft\'s Copilot JumpStart incentives apply to qualifying engagements — check eligibility',
        ],
      },
    ],
    mermaidDiagram: `flowchart LR
    subgraph Analyse
        A[Run Copilot\\nreadiness scan] --> B[Score users by\\nM365 engagement]
    end
    subgraph Recommend
        C[Identify\\npilot group] --> D[Present proposal\\nwith cost estimate]
    end
    subgraph Pilot
        E[Deploy Copilot to\\nrecommended users] --> F[Review adoption\\nand feedback]
    end
    subgraph Expand
        G[Build business case\\nfor full rollout]
    end
    Analyse --> Recommend --> Pilot --> Expand`,
    keyTalkingPoints: [
      '"The more a user engages with the Microsoft ecosystem, the more value they\'ll get from Copilot."',
      '"We\'re not guessing who should get Copilot — the activity data tells us."',
      '"These are your internal champions. Once they have it, they\'ll miss it if you take it away."',
      '"We can estimate the pilot cost right now and build a 90-day plan around it."',
    ],
  },
]
